/**
 * Позы упражнений — по одной пиктограмме на каждое движение справочника.
 *
 * Номера — те же, что в `engine/catalog.py`: тест `tests/test_figures.py`
 * следит, чтобы у каждого каталожного движения была фигурка и чтобы лишних не
 * завелось. У личных движений фигурок нет: как выглядит упражнение, которое
 * человек придумал сам, приложение знать не может.
 *
 * КАК ЧИТАТЬ ПОЗУ. Холст 100×100, y растёт вниз, пол обычно на 93. `a` —
 * начало повторения, `b` — другой его конец. Задаются таз, наклон корпуса,
 * кисти и стопы; локти и колени движок ставит сам (см. `engine.ts`). Сустав
 * поставлен рукой (`elbowAt`, `kneeAt`) только там, где проекция укорачивает
 * кость: локоть в жиме уходит на зрителя, бедро сидящего спереди смотрит в
 * экран.
 *
 * Цифры здесь — про КАРТИНКУ, а не про нагрузку: это координаты, и к весам,
 * повторам и прогнозам они отношения не имеют (принцип №1 их не касается).
 */

import { add, dir, type FigureSpec, type Prim, type Pt } from './engine'

const FLOOR = 93
/** Лодыжка стоящего человека: стопа толщиной 8 ложится ровно на пол. */
const ANKLE = 89

/** Точка относительно плеч: `fwd` — вперёд от груди, `up` — вдоль корпуса к голове. */
function rel(hip: Pt, torso: number, fwd: number, up: number): Pt {
  const shoulder = add(hip, dir(torso), 25)
  return add(add(shoulder, dir(torso + 90), fwd), dir(torso), up)
}

/** Подушка вдоль спины: от `from` до `to` единиц от таза, с обратной стороны корпуса. */
function backPad(hip: Pt, torso: number, from: number, to: number): Prim {
  const length = to - from
  const center = add(add(hip, dir(torso), (from + to) / 2), dir(torso - 90), 9.4)
  return {
    k: 'rect',
    x: center[0] - length / 2,
    y: center[1] - 3,
    w: length,
    h: 6,
    rot: torso - 90,
  }
}

const seat = (x: number, y: number, w = 20): Prim => ({ k: 'rect', x, y, w, h: 5.5 })
const post = (x: number, top: number, bottom = FLOOR): Prim => ({
  k: 'line',
  a: [x, top],
  b: [x, bottom],
  w: 3.4,
  tone: 'prop',
})
/** Грузовой стек тренажёра у правого края кадра. */
const stack = (x = 87, top = 16): Prim => ({ k: 'rect', x, y: top, w: 8, h: FLOOR - top, r: 2.5 })

/** Горизонтальная скамья: подушка и две ножки. */
function flatBench(x: number, w: number, top = 64): Prim[] {
  return [
    { k: 'rect', x, y: top, w, h: 6 },
    post(x + 8, top + 6),
    post(x + w - 8, top + 6),
  ]
}

// --- жим лёжа и его родня: лёжа на спине, голова слева --------------------- //

const LYING_HIP: Pt = [58, 57.5]
const lying = {
  hip: LYING_HIP,
  torso: -90,
  foot: [76, ANKLE] as Pt,
}

const INCLINE_HIP: Pt = [60, 67]
const INCLINE = -60
const incline = {
  hip: INCLINE_HIP,
  torso: INCLINE,
  foot: [81, ANKLE] as Pt,
}
const inclineBench: Prim[] = [backPad(INCLINE_HIP, INCLINE, -4, 40), seat(54, 73.5, 18), post(34, 66), post(64, 79)]

// --- сидя в тренажёре: спина у подушки, стопы на полу ---------------------- //

const SEATED_HIP: Pt = [36, 64]
const seatedMachine = (torso: number): Prim[] => [
  backPad(SEATED_HIP, torso, -2, 34),
  seat(30, 70.5, 22),
  post(40, 76),
]

const STAND_HIP: Pt = [48, 48.8]

export const FIGURES: Record<number, FigureSpec> = {
  // ----------------------------------------------------------------------- //
  // Грудь
  // ----------------------------------------------------------------------- //
  1: {
    label: 'Жим лёжа со штангой',
    view: 'side',
    a: { ...lying, hand: [33.5, 32], elbowAt: [33.5, 45] },
    b: { ...lying, hand: [36, 49], elbowAt: [42, 66] },
    prims: [...flatBench(12, 60), { k: 'plate', at: 'hand', d: [0, -1] }],
    floor: FLOOR,
  },
  2: {
    label: 'Жим лёжа на наклонной скамье со штангой',
    view: 'side',
    a: { ...incline, hand: [40, 27], elbowAt: [39.5, 40] },
    b: { ...incline, hand: [44, 43], elbowAt: [49, 59] },
    prims: [...inclineBench, { k: 'plate', at: 'hand', d: [0, -1] }],
    floor: FLOOR,
  },
  3: {
    label: 'Жим гантелей лёжа',
    view: 'side',
    a: { ...lying, hand: [33.5, 32], elbowAt: [33.5, 45] },
    b: { ...lying, hand: [37, 49], elbowAt: [43, 66] },
    prims: [...flatBench(12, 60), { k: 'plate', at: 'hand', r: 5.6, d: [0, -1] }],
    floor: FLOOR,
  },
  4: {
    label: 'Жим гантелей на наклонной скамье',
    view: 'side',
    a: { ...incline, hand: [40, 27], elbowAt: [39.5, 40] },
    b: { ...incline, hand: [45, 43], elbowAt: [50, 59] },
    prims: [...inclineBench, { k: 'plate', at: 'hand', r: 5.6, d: [0, -1] }],
    floor: FLOOR,
  },
  5: {
    label: 'Жим от груди в тренажёре',
    view: 'side',
    a: { hip: SEATED_HIP, torso: -8, hand: [45, 45], elbowAt: [36, 51], foot: [58, ANKLE] },
    b: { hip: SEATED_HIP, torso: -8, hand: [58.5, 44], elbowAt: [46, 45.5], foot: [58, ANKLE] },
    prims: [
      ...seatedMachine(-8),
      stack(),
      { k: 'line', a: [62, 10], b: 'hand', w: 3, tone: 'prop' },
      { k: 'line', a: 'hand', b: 'hand', da: [0, -5], db: [0, 5], w: 4, tone: 'gear' },
    ],
    floor: FLOOR,
  },
  6: {
    label: 'Отжимания на брусьях',
    view: 'side',
    a: { hip: [45, 49.5], torso: 12, hand: [50, 50.5], foot: [36, 82], toe: 190 },
    b: { hip: [44, 61], torso: 22, hand: [50, 50.5], foot: [33, 91], toe: 200 },
    prims: [
      post(34, 54, 98),
      post(68, 54, 98),
      { k: 'line', a: [28, 54], b: [74, 54], w: 3.4, tone: 'gear', back: true },
    ],
    still: 0.6,
  },
  7: {
    label: 'Отжимания от пола',
    view: 'side',
    a: { hip: [49.9, 71.9], torso: 69.6, neck: 75, hand: [73.5, 89], foot: [12, 86], toe: 172 },
    b: { hip: [51.8, 79], torso: 80, neck: 82, hand: [73.5, 89], foot: [12, 86], toe: 172 },
    floor: FLOOR,
    // Фигура прижата к полу — кадр сдвинут вниз, чтобы она стояла по центру.
    box: [0, 25, 100],
    still: 0.7,
  },
  66: {
    label: 'Разведение в тренажёре (бабочка)',
    view: 'front',
    a: { hip: [50, 70], hand: [84, 45], elbow: -1, foot: [59, 94], kneeAt: [59, 75] },
    b: { hip: [50, 70], hand: [53.5, 47], elbow: -1, arm: 0.55, foot: [59, 94], kneeAt: [59, 75] },
    prims: [
      { k: 'rect', x: 40, y: 34, w: 20, h: 40, r: 5 },
      seat(38, 74, 24),
      { k: 'line', a: [36, 16], b: 'haL', w: 3, tone: 'prop' },
      { k: 'line', a: [64, 16], b: 'haR', w: 3, tone: 'prop' },
      { k: 'line', a: 'haR', b: 'haR', da: [0, -6], db: [0, 6], w: 4.4, tone: 'gear' },
      { k: 'line', a: 'haL', b: 'haL', da: [0, -6], db: [0, 6], w: 4.4, tone: 'gear' },
    ],
    still: 0.35,
  },

  // ----------------------------------------------------------------------- //
  // Плечи
  // ----------------------------------------------------------------------- //
  10: {
    label: 'Жим стоя со штангой',
    view: 'side',
    a: { hip: STAND_HIP, torso: 0, hand: [53, 21], elbowAt: [54, 34], foot: [50, ANKLE] },
    b: { hip: STAND_HIP, torso: 0, hand: [49.5, -2], elbowAt: [50, 11], foot: [50, ANKLE] },
    prims: [{ k: 'plate', at: 'hand', d: [0, -1] }],
    floor: FLOOR,
    box: [-7, -16, 114],
  },
  11: {
    label: 'Жим гантелей сидя',
    view: 'side',
    a: { hip: [42, 62], torso: -4, hand: [45, 29], elbowAt: [45, 42], foot: [62, ANKLE] },
    b: { hip: [42, 62], torso: -4, hand: [43.5, 8], elbowAt: [44, 21.5], foot: [62, ANKLE] },
    prims: [
      backPad([42, 62], -4, -2, 32),
      seat(36, 68.5, 22),
      post(46, 74),
      { k: 'plate', at: 'hand', r: 5.6, d: [0, -1] },
    ],
    floor: FLOOR,
    box: [-3, -4, 104],
  },
  12: {
    label: 'Жим вверх в тренажёре',
    view: 'side',
    a: { hip: SEATED_HIP, torso: -4, hand: [39, 32], elbowAt: [39, 45], foot: [58, ANKLE] },
    b: { hip: SEATED_HIP, torso: -4, hand: [38, 11], elbowAt: [38.5, 24.5], foot: [58, ANKLE] },
    prims: [
      ...seatedMachine(-4),
      stack(),
      { k: 'line', a: [80, 24], b: 'hand', w: 3, tone: 'prop' },
      { k: 'line', a: 'hand', b: 'hand', da: [-5, 0], db: [5, 0], w: 4, tone: 'gear' },
    ],
    floor: FLOOR,
    box: [-3, -4, 104],
  },
  60: {
    label: 'Махи гантелями в стороны',
    view: 'front',
    a: { hip: [50, 49], hand: [63, 50], elbow: -1, foot: [55.5, 89.5] },
    b: { hip: [50, 49], hand: [84, 27.5], elbow: -1, foot: [55.5, 89.5] },
    prims: [
      { k: 'plate', at: 'haR', r: 5.6 },
      { k: 'plate', at: 'haL', r: 5.6 },
    ],
    floor: FLOOR,
    still: 0.75,
  },
  61: {
    label: 'Тяга к лицу в блоке',
    view: 'side',
    a: { hip: [38, 48.8], torso: -3, hand: [63, 25.5], elbowAt: [50, 25], foot: [42, ANKLE], footF: [33, ANKLE] },
    b: { hip: [38, 48.8], torso: -6, hand: [46.5, 17.5], elbowAt: [33, 21], foot: [42, ANKLE], footF: [33, ANKLE] },
    prims: [
      stack(88, 8),
      { k: 'disc', at: [88, 20], r: 3.2, tone: 'prop' },
      { k: 'line', a: [88, 20], b: 'hand', w: 1.8, tone: 'prop' },
      { k: 'line', a: 'hand', b: 'hand', da: [0, -4.5], db: [0, 4.5], w: 3.6, tone: 'gear' },
    ],
    floor: FLOOR,
    still: 0.7,
  },

  // ----------------------------------------------------------------------- //
  // Спина
  // ----------------------------------------------------------------------- //
  20: {
    label: 'Тяга штанги в наклоне',
    view: 'side',
    a: { hip: [36, 56], torso: 62, neck: 45, hand: [58.5, 69.5], foot: [46, ANKLE] },
    b: { hip: [36, 56], torso: 62, neck: 45, hand: [50, 58], foot: [46, ANKLE] },
    prims: [{ k: 'plate', at: 'hand', r: 9, d: [0, 1.5] }],
    floor: FLOOR,
    still: 0.6,
  },
  21: {
    label: 'Тяга гантели в наклоне',
    view: 'side',
    a: {
      hip: [40, 49],
      torso: 72,
      neck: 62,
      hand: [63, 67],
      handF: [66, 59],
      foot: [33, ANKLE],
      footF: [37, 57.5],
      kneeFAt: [57, 58.5],
      toeF: -95,
    },
    b: {
      hip: [40, 49],
      torso: 72,
      neck: 62,
      hand: [55, 52],
      handF: [66, 59],
      foot: [33, ANKLE],
      footF: [37, 57.5],
      kneeFAt: [57, 58.5],
      toeF: -95,
    },
    prims: [...flatBench(28, 66, 62.5), { k: 'db', at: 'hand', ang: 90, d: [0, 2] }],
    floor: FLOOR,
    still: 0.6,
  },
  22: {
    label: 'Тяга горизонтального блока',
    view: 'side',
    a: { hip: [36, 70], torso: 10, hand: [63, 53], foot: [72, 71], toe: 8 },
    b: { hip: [36, 70], torso: -6, hand: [46, 56], foot: [72, 71], toe: 8 },
    prims: [
      { k: 'rect', x: 26, y: 76.5, w: 34, h: 5.5 },
      post(34, 82),
      post(54, 82),
      stack(88, 22),
      { k: 'line', a: [79, 58], b: [79, 84], w: 4, tone: 'prop' },
      { k: 'disc', at: [88, 54], r: 3.2, tone: 'prop' },
      { k: 'line', a: [88, 54], b: 'hand', w: 1.8, tone: 'prop' },
      { k: 'line', a: 'hand', b: 'hand', da: [0, -4.5], db: [0, 4.5], w: 3.6, tone: 'gear' },
    ],
    floor: FLOOR,
    still: 0.6,
  },
  23: {
    label: 'Горизонтальная тяга в тренажёре',
    view: 'side',
    a: { hip: [32, 64], torso: 5, hand: [60, 45], foot: [54, ANKLE] },
    b: { hip: [32, 64], torso: 5, hand: [43, 49], foot: [54, ANKLE] },
    prims: [
      seat(24, 70.5, 22),
      post(34, 76),
      { k: 'rect', x: 43, y: 36, w: 6, h: 26, r: 3 },
      { k: 'line', a: [46, 62], b: [56, FLOOR], w: 3.4, tone: 'prop' },
      stack(),
      { k: 'line', a: [74, 88], b: 'hand', w: 3, tone: 'prop' },
      { k: 'line', a: 'hand', b: 'hand', da: [0, -5], db: [0, 5], w: 4, tone: 'gear', back: false },
    ],
    floor: FLOOR,
    still: 0.6,
  },
  30: {
    label: 'Подтягивания',
    view: 'front',
    a: { hip: [50, 59.6], hand: [69, 12], foot: [54, 97] },
    b: { hip: [50, 43.5], hand: [69, 12], foot: [54, 81] },
    prims: [{ k: 'line', a: [10, 12], b: [90, 12], w: 3.4, tone: 'gear', back: true }],
    still: 0.55,
  },
  31: {
    label: 'Подтягивания обратным хватом',
    view: 'front',
    a: { hip: [50, 61.2], hand: [60.5, 12], foot: [54, 98] },
    b: { hip: [50, 44], hand: [60.5, 12], elbowAt: [65, 31], foot: [54, 81] },
    prims: [{ k: 'line', a: [18, 12], b: [82, 12], w: 3.4, tone: 'gear', back: true }],
    still: 0.55,
  },
  32: {
    label: 'Тяга верхнего блока',
    view: 'front',
    a: { hip: [50, 72], hand: [73, 25], foot: [58, 94], kneeAt: [58, 77] },
    b: { hip: [50, 72], hand: [71, 46], foot: [58, 94], kneeAt: [58, 77] },
    prims: [
      seat(38, 76, 24),
      { k: 'line', a: [50, -4], b: 'grip', w: 1.8, tone: 'prop' },
      { k: 'bar', a: 'haL', b: 'haR', over: 7 },
    ],
    still: 0.6,
  },

  // ----------------------------------------------------------------------- //
  // Ноги
  // ----------------------------------------------------------------------- //
  40: {
    label: 'Приседания со штангой',
    view: 'side',
    a: { hip: [47, 48.8], torso: 3, hand: rel([47, 48.8], 3, -5.3, 0), elbow: -1, foot: [50, ANKLE] },
    b: { hip: [38, 67], torso: 40, neck: 28, hand: rel([38, 67], 40, -5.3, 0), elbow: -1, foot: [50, ANKLE] },
    prims: [{ k: 'plate', at: 'hand', d: [1.5, 0.5], back: true }],
    floor: FLOOR,
    still: 0.75,
  },
  41: {
    label: 'Фронтальные приседания',
    view: 'side',
    a: { hip: [47, 48.8], torso: 0, hand: rel([47, 48.8], 0, 2.5, 4.5), foot: [50, ANKLE] },
    b: { hip: [40, 67.5], torso: 20, neck: 12, hand: rel([40, 67.5], 20, 2.5, 4.5), foot: [50, ANKLE] },
    prims: [{ k: 'plate', at: 'hand', d: [2, 2.5] }],
    floor: FLOOR,
    still: 0.75,
  },
  42: {
    label: 'Жим ногами',
    view: 'side',
    a: { hip: [34, 76], torso: -45, neck: -35, hand: [42, 80], foot: [56, 58], toe: -35 },
    b: { hip: [34, 76], torso: -45, neck: -35, hand: [42, 80], foot: [64.5, 49.5], toe: -35 },
    prims: [
      backPad([34, 76], -45, -2, 36),
      { k: 'rect', x: 26, y: 83, w: 22, h: 5.5 },
      { k: 'line', a: [44, 90], b: [92, 42], w: 3.4, tone: 'prop' },
      { k: 'line', a: [20, FLOOR], b: [60, FLOOR], w: 3.4, tone: 'prop' },
      { k: 'line', a: 'foot', b: 'foot', da: [-7.1, -14.2], db: [8.5, 1.4], w: 4.4, tone: 'gear' },
    ],
    still: 0.35,
  },
  43: {
    label: 'Болгарский сплит-присед с гантелями',
    view: 'side',
    a: { hip: [45, 50.5], torso: 6, hand: [48, 51.5], foot: [56, ANKLE], footF: [22, 62], toeF: -100 },
    b: { hip: [44, 66], torso: 12, hand: [49.5, 67.5], foot: [56, ANKLE], footF: [22, 62], toeF: -100 },
    prims: [
      { k: 'rect', x: 4, y: 66, w: 28, h: 6 },
      post(10, 72),
      post(26, 72),
      { k: 'db', at: 'hand', ang: 90, d: [0, 2] },
    ],
    floor: FLOOR,
    still: 0.7,
  },
  67: {
    label: 'Разгибание ног в тренажёре',
    view: 'side',
    a: { hip: [36, 61], torso: -12, hand: [41, 69], foot: [54, 82.5], kneeAt: [56, 62.5], toe: 93 },
    b: { hip: [36, 61], torso: -12, hand: [41, 69], foot: [76.4, 60], kneeAt: [56, 62.5], toe: 8 },
    prims: [
      backPad([36, 61], -12, -2, 34),
      seat(28, 67.5, 30),
      post(40, 73),
      { k: 'line', a: [56, 66], b: { from: 'knee', to: 'foot', t: 0.92, off: -6 }, w: 3, tone: 'prop' },
      { k: 'disc', at: { from: 'knee', to: 'foot', t: 0.92, off: -6 }, r: 4.2, tone: 'gear' },
    ],
    floor: FLOOR,
    still: 0.65,
  },
  68: {
    label: 'Сгибание ног в тренажёре',
    view: 'side',
    a: { hip: [52, 56], torso: 90, neck: 78, hand: [80, 69], foot: [11.5, 57.5], kneeAt: [32, 56.5], toe: 205 },
    b: { hip: [52, 56], torso: 90, neck: 78, hand: [80, 69], foot: [37, 36.5], kneeAt: [32, 56.5], toe: -65 },
    prims: [
      ...flatBench(28, 64, 62.5),
      { k: 'line', a: [32, 62], b: { from: 'knee', to: 'foot', t: 0.9, off: 6 }, w: 3, tone: 'prop', back: false },
      { k: 'disc', at: { from: 'knee', to: 'foot', t: 0.9, off: 6 }, r: 4.2, tone: 'gear' },
    ],
    floor: FLOOR,
    still: 0.6,
  },
  69: {
    label: 'Подъёмы на носки в тренажёре',
    view: 'side',
    a: { hip: [48.5, 49.4], torso: 0, hand: [52, 22], foot: [48.6, 89.5], toe: 80 },
    b: { hip: [50, 43.2], torso: 0, hand: [53.5, 15.8], foot: [50, 83.1], toe: 125 },
    prims: [
      { k: 'rect', x: 8, y: 6, w: 7, h: 87, r: 3 },
      { k: 'rect', x: 50, y: 90.5, w: 16, h: 5, r: 2 },
      { k: 'line', a: [12, 40], b: 'shoulder', db: [-9, -6.5], w: 3, tone: 'prop' },
      { k: 'line', a: 'shoulder', b: 'shoulder', da: [-10, -6.5], db: [-1, -6.5], w: 5.5, tone: 'gear' },
    ],
    floor: 96,
    box: [0, 0, 100],
  },

  // ----------------------------------------------------------------------- //
  // Задняя цепь
  // ----------------------------------------------------------------------- //
  50: {
    label: 'Становая тяга',
    view: 'side',
    a: { hip: [31, 64], torso: 66, neck: 40, hand: [54.5, 79.5], foot: [48, ANKLE] },
    b: { hip: [47.5, 48.8], torso: 0, neck: 0, hand: [50.5, 49.5], foot: [48, ANKLE] },
    prims: [{ k: 'plate', at: 'hand', r: 11, d: [0, 2.5] }],
    floor: FLOOR,
    still: 0.2,
  },
  51: {
    label: 'Румынская тяга со штангой',
    view: 'side',
    a: { hip: [47.5, 48.8], torso: 0, neck: 0, hand: [50.5, 49.5], foot: [48, ANKLE] },
    b: { hip: [40, 50], torso: 70, neck: 50, hand: [63.5, 67], foot: [48, ANKLE] },
    prims: [{ k: 'plate', at: 'hand', r: 11, d: [0, 2.5] }],
    floor: FLOOR,
    still: 0.8,
  },
  52: {
    label: 'Ягодичный мост со штангой',
    view: 'side',
    a: { hip: [45.6, 71.6], torso: -50, neck: -15, hand: [48, 63.5], foot: [68, ANKLE] },
    b: { hip: [51.4, 58], torso: -84, neck: -60, hand: [54, 50], foot: [68, ANKLE] },
    prims: [
      { k: 'rect', x: 4, y: 60.5, w: 27, h: 6.5 },
      post(10, 67),
      post(25, 67),
      { k: 'plate', at: 'hip', r: 9, d: [2.5, -8.5] },
    ],
    floor: FLOOR,
    box: [0, 14, 100],
    still: 0.8,
  },
  53: {
    label: 'Гиперэкстензия',
    view: 'side',
    a: { hip: [50.6, 59.4], torso: 140, hand: [60, 71], foot: [22, 88], toe: 135 },
    b: { hip: [50.6, 59.4], torso: 45, hand: [60, 46], foot: [22, 88], toe: 135 },
    prims: [
      { k: 'rect', x: 44.5, y: 65, w: 14, h: 6.5, rot: 45 },
      { k: 'line', a: [52, 70], b: [58, FLOOR], w: 3.4, tone: 'prop' },
      { k: 'line', a: [14, FLOOR], b: [66, FLOOR], w: 3.4, tone: 'prop' },
      { k: 'disc', at: [17.5, 83.5], r: 4, tone: 'prop' },
    ],
    still: 0.7,
  },

  // ----------------------------------------------------------------------- //
  // Руки
  // ----------------------------------------------------------------------- //
  62: {
    label: 'Подъём гантелей на бицепс',
    view: 'side',
    a: { hip: STAND_HIP, torso: 0, hand: [50.5, 49.5], elbowAt: [48.5, 37], foot: [50, ANKLE] },
    b: { hip: STAND_HIP, torso: 0, hand: [54.5, 25.5], elbowAt: [48.5, 37], foot: [50, ANKLE] },
    prims: [{ k: 'plate', at: 'hand', r: 5.6 }],
    floor: FLOOR,
    still: 0.65,
  },
  63: {
    label: 'Сгибания на бицепс в блоке',
    view: 'side',
    a: { hip: [40, 48.8], torso: 0, hand: [43.5, 49.5], elbowAt: [40.5, 37], foot: [42, ANKLE] },
    b: { hip: [40, 48.8], torso: 0, hand: [47, 25.5], elbowAt: [40.5, 37], foot: [42, ANKLE] },
    prims: [
      stack(88, 22),
      { k: 'disc', at: [84, 88], r: 3.2, tone: 'prop' },
      { k: 'line', a: [84, 88], b: 'hand', w: 1.8, tone: 'prop' },
      { k: 'line', a: 'hand', b: 'hand', da: [-4.5, 0], db: [4.5, 0], w: 3.6, tone: 'gear' },
    ],
    floor: FLOOR,
    still: 0.65,
  },
  64: {
    label: 'Разгибания на трицепс в блоке',
    view: 'side',
    a: { hip: [38, 48.8], torso: 8, hand: [54, 29.5], elbowAt: [43.5, 37], foot: [42, ANKLE] },
    b: { hip: [38, 48.8], torso: 8, hand: [47.5, 49.5], elbowAt: [43.5, 37], foot: [42, ANKLE] },
    prims: [
      stack(88, 4),
      { k: 'disc', at: [66, 6], r: 3.2, tone: 'prop' },
      { k: 'line', a: [66, 6], b: [88, 6], w: 3, tone: 'prop' },
      { k: 'line', a: [66, 6], b: 'hand', w: 1.8, tone: 'prop' },
      { k: 'line', a: 'hand', b: 'hand', da: [-4.5, 0], db: [4.5, 0], w: 3.6, tone: 'gear' },
    ],
    floor: FLOOR,
    still: 0.4,
  },
  65: {
    label: 'Французский жим с гантелями',
    view: 'side',
    a: { ...lying, hand: [33.5, 32], elbowAt: [33.5, 45] },
    b: { ...lying, hand: [20.8, 43], elbowAt: [33.5, 45] },
    prims: [...flatBench(12, 60), { k: 'db', at: 'hand', across: ['elbow', 'hand'] }],
    floor: FLOOR,
    still: 0.5,
  },

  // ----------------------------------------------------------------------- //
  // Пресс
  // ----------------------------------------------------------------------- //
  80: {
    label: 'Планка',
    view: 'side',
    a: { hip: [49.9, 79.8], torso: 81.2, neck: 78, hand: [88, 89.5], elbowAt: [75, 89.5], foot: [10, 86], toe: 172 },
    floor: FLOOR,
    box: [0, 28, 100],
  },
  81: {
    label: 'Подъём ног в висе',
    view: 'side',
    a: { hip: [50, 60], torso: 0, hand: [50, 9.5], foot: [51, 100], toe: 125 },
    b: { hip: [53.5, 59.8], torso: -8, hand: [50, 9.5], foot: [92, 57], toe: 12 },
    prims: [{ k: 'disc', at: [50, 7], r: 3.2, tone: 'gear', back: true }],
    box: [-5, -2, 112],
    still: 0.7,
  },
  82: {
    label: 'Скручивания в блоке',
    view: 'side',
    a: { hip: [40, 69], torso: 25, neck: 40, hand: [58, 37], foot: [22, 88], toe: 252 },
    b: { hip: [38, 68], torso: 95, neck: 130, hand: [70, 69], foot: [22, 88], toe: 252 },
    prims: [
      stack(88, 4),
      { k: 'disc', at: [80, 6], r: 3.2, tone: 'prop' },
      { k: 'line', a: [80, 6], b: [88, 6], w: 3, tone: 'prop' },
      { k: 'line', a: [80, 6], b: 'hand', w: 1.8, tone: 'prop' },
      { k: 'line', a: 'hand', b: 'hand', da: [-3, -3], db: [3, 3], w: 3.6, tone: 'gear' },
    ],
    floor: FLOOR,
    still: 0.6,
  },
}

export function hasFigure(exerciseId: number): boolean {
  return exerciseId in FIGURES
}
